import { useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { api } from "@/lib/api";

function getSessionId() {
  let s = localStorage.getItem("btc_chat_session");
  if (!s) { s = "chat-" + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem("btc_chat_session", s); }
  return s;
}
function getSource() {
  const p = new URLSearchParams(window.location.search);
  return p.get("utm_source") || p.get("from") || (document.referrer ? new URL(document.referrer).hostname : "") || "website";
}

const GREETING = "Hi, I'm Bella 💄 Welcome to Beauty Trade Club! Looking to buy, sell or trade beauty? Ask me anything — or say hi!";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: GREETING }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const sessionId = useRef(getSessionId());
  const source = useRef(getSource());

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open, sending]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setSending(true);
    try {
      const { data } = await api.post("/chatbot/message", {
        session_id: sessionId.current, message: text, source: source.current,
      });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Oops, I glitched! Try again in a sec 💗" }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <button
          data-testid="chat-widget-toggle"
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-50 flex items-center gap-2 bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full pl-4 pr-5 py-3 shadow-[0_10px_30px_rgba(232,149,107,0.5)] transition-all hover:scale-105"
        >
          <MessageCircle size={20} /> Chat with Bella
        </button>
      )}

      {open && (
        <div
          data-testid="chat-widget-panel"
          className="fixed bottom-5 right-5 z-50 w-[92vw] max-w-sm h-[520px] max-h-[80vh] bg-white rounded-3xl shadow-2xl border border-neutral-200 flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-300"
        >
          <div className="bg-[#E8956B] text-white px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center"><Sparkles size={18} /></div>
              <div>
                <div className="font-bold leading-tight">Bella</div>
                <div className="text-[11px] text-white/85 leading-tight">Beauty Trade Club assistant</div>
              </div>
            </div>
            <button data-testid="chat-widget-close" onClick={() => setOpen(false)} className="text-white/85 hover:text-white"><X size={20} /></button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-neutral-50">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  data-testid={`chat-msg-${m.role}`}
                  className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    m.role === "user" ? "bg-[#E8956B] text-white rounded-br-md" : "bg-white border border-neutral-200 text-neutral-800 rounded-bl-md"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-white border border-neutral-200 rounded-2xl rounded-bl-md px-4 py-3">
                  <span className="flex gap-1">
                    <span className="w-2 h-2 bg-neutral-300 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <span className="w-2 h-2 bg-neutral-300 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <span className="w-2 h-2 bg-neutral-300 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="p-3 border-t border-neutral-200 bg-white flex items-center gap-2">
            <input
              data-testid="chat-widget-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Type a message..."
              className="flex-1 bg-neutral-100 rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#E8956B]/40"
            />
            <button
              data-testid="chat-widget-send"
              onClick={send}
              disabled={sending || !input.trim()}
              className="bg-[#E8956B] hover:bg-[#CE744A] text-white rounded-full p-2.5 transition-all disabled:opacity-50"
            >
              <Send size={18} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
